import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import './UserGrowthModal.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const UserGrowthModal = ({ isOpen, onClose }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user growth data from backend
  const fetchUserGrowthData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/dashboard/user-growth`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user growth data');
      }

      const data = await response.json();
      
      // Transform backend data to chart format
      const transformedData = {
        labels: data.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'User Signups',
            data: data.values || [120, 190, 300, 500, 200, 300],
            fill: true,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: '#36A2EB',
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          }
        ]
      };
      
      setChartData(transformedData);
    } catch (err) {
      console.error('Error fetching user growth data:', err);
      setError(err.message);
      // Fallback to mock data
      setChartData({
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'User Signups',
            data: [120, 190, 300, 500, 200, 300],
            fill: true,
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderColor: '#36A2EB',
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchUserGrowthData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#36A2EB',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          padding: 10,
        },
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false,
        },
        beginAtZero: true,
        ticks: {
          padding: 10,
        },
      },
    },
    elements: {
      point: {
        radius: 4,
        hoverRadius: 6,
      },
      line: {
        tension: 0.4,
      },
    },
  };

  // Create portal to render modal outside of parent component
  return createPortal(
    <div className="user-growth-modal-overlay">
      <div className="user-growth-modal-content">
        <button
          className="user-growth-modal-close"
          onClick={onClose}
        >
          &times;
        </button>
        <h2 className="user-growth-modal-title">📈 User Growth Chart</h2>
        
        {loading && (
          <div className="user-growth-modal-loading">
            <div className="loading-spinner"></div>
            <p>Loading user growth data...</p>
          </div>
        )}
        
        {error && (
          <div className="user-growth-modal-error">
            <p>⚠️ Error loading data: {error}</p>
            <button onClick={fetchUserGrowthData}>Retry</button>
          </div>
        )}
        
        {!loading && !error && (
          <div className="user-growth-modal-chart">
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default UserGrowthModal; 